import { Route, UrlSegment } from '@angular/router';
import { AuthGuard } from '@apruv-client/services';
import { RoleGuard } from './shared/services/roleGuard.service';
import { AuthBootstrapResolver } from './resolvers/auth-bootstrap.resolver';
import { isPublicLinkUrl } from './public-links';

export const appRoutes: Route[] = [
    {
        path: 'auth/delegation',
        loadComponent: () => import('./delegation/delegation-launch.component').then((c) => c.DelegationLaunchComponent),
        canActivate: [AuthGuard],
    },
    {
        path: 'test/verify-v2/flow-harness',
        loadComponent: () => import('./verify-v2/testing/flow-harness.component').then((c) => c.FlowHarnessComponent),
        data: { skipAuth: true, publicRoute: true },
    },
    {
        path: 'verify',
        data: { breadcrumb: 'Verify', allowedRoles: ['verify_app']},
        canActivate: [AuthGuard, RoleGuard],
        resolve: { bootstrap: AuthBootstrapResolver },
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./verify-v2/v1-verify-redirect.component').then((c) => c.VerifyV1RedirectComponent),
            },
            {
                path: 'sales',
                pathMatch: 'full',
                loadComponent: () => import('./verify-v2/v1-verify-redirect.component').then((c) => c.VerifyV1RedirectComponent),
            },
            {
                path: '**',
                loadComponent: () => import('./verify-v2/v1-verify-redirect.component').then((c) => c.VerifyV1RedirectComponent),
            },
        ],
    },
    {
        path: 'test/verify-v2/preflight-harness',
        loadComponent: () => import('./verify-v2/testing/preflight-harness.component').then((c) => c.PreflightHarnessComponent),
        data: { skipAuth: true, publicRoute: true },
    },
    {
        path: 'test/verify-v2/media-harness',
        loadComponent: () => import('./verify-v2/testing/media-harness.component').then((c) => c.MediaHarnessComponent),
        data: { skipAuth: true, publicRoute: true },
    },
    {
        path: 'test/verify-v2/browser-gate-harness',
        loadComponent: () => import('./verify-v2/testing/browser-gate-harness.component').then((c) => c.BrowserGateHarnessComponent),
        data: { skipAuth: true, publicRoute: true },
    },
    {
        path: 'test/verify-v2/recording-harness',
        loadComponent: () => import('./verify-v2/testing/recording-harness.component').then((c) => c.RecordingHarnessComponent),
        data: { skipAuth: true, publicRoute: true },
    },
    {
        path: '',
        loadComponent: () => import('./layout/layout.component').then((c) => c.LayoutComponent),
        canActivate: [AuthGuard, RoleGuard],
        resolve: { bootstrap: AuthBootstrapResolver },
        data: { isRootPath: true },
        loadChildren: () => import('./layout/layout.routes').then((c) => c.LAYOUT_ROUTES),
    },
    {
        path: 'review',
        loadComponent: () => import('./review/review.component').then((c) => c.ReviewComponent),
        loadChildren: () => import('./review/review.route').then((c) => c.REVIEW_ROUTES),
    },
    {
        path: 'referral',
        loadComponent: () => import('./referral/referral.component').then((c) => c.ReferralComponent),
        loadChildren: () => import('./referral/referral.route').then((c) => c.REFERRAL_ROUTES),
    },
    {
        path: 'technician',
        loadComponent: () => import('./technician/technician.component').then((c) => c.TechnicianComponent),
        data: { breadcrumb: 'Technician', allowedRoles: ['technician_app']},
        canActivate: [AuthGuard, RoleGuard],
        resolve: { bootstrap: AuthBootstrapResolver },
        loadChildren: () => import('./technician/technician.routes').then((c) => c.TECHNICIAN_ROUTES),
    },
    {
        path: 'notifications',
        data: { breadcrumb: 'Technician', allowedRoles: ['technician_app']},
        canActivate: [AuthGuard, RoleGuard],
        resolve: { bootstrap: AuthBootstrapResolver },
        loadComponent: () => import('./technician/notifications/notifications.component').then((c) => c.NotificationsComponent),
    },
    {
        path: 'onboarding',
        loadComponent: () => import('./onboarding/onboarding.component').then((c) => c.OnboardingComponent),
        loadChildren: () => import('./onboarding/onboarding.routes').then((c) => c.ONBOARDING_ROUTES),
    },
    {
        path: 'registration/:company_id',
        loadComponent: () => import('./sales-registration/sales-registration.component').then((c) => c.SalesRegistrationComponent),
    },
    {
        path: 'v1/verify',
        data: { breadcrumb: 'Verify', allowedRoles: ['verify_app']},
        canActivate: [AuthGuard, RoleGuard],
        resolve: { bootstrap: AuthBootstrapResolver },
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./verify-v2/v1-verify-redirect.component').then((c) => c.VerifyV1RedirectComponent),
            },
            {
                path: '**',
                loadComponent: () => import('./verify-v2/v1-verify-redirect.component').then((c) => c.VerifyV1RedirectComponent),
            },
        ],
    },
    {
        path: 'v2/verify',
        data: { breadcrumb: 'Verify', allowedRoles: ['verify_app', 'technician_app']},
        canActivate: [AuthGuard, RoleGuard],
        resolve: { bootstrap: AuthBootstrapResolver },
        loadComponent: () => import('./verify-v2/verify.component').then((c) => c.VerifyComponent),
        loadChildren: () => import('./verify-v2/verify.route').then((c) => c.Verify_V2_ROUTES),
    },
    {
        path: 'access-denied',
        loadComponent: () => import('./accessdenied/accessdenied.component').then((c) => c.AccessDeniedComponent),
        data: { skipAuth: true, publicRoute: true },
    },
    {
        path: 'link-unavailable',
        loadComponent: () => import('./link-unavailable/link-unavailable.component').then((c) => c.LinkUnavailableComponent),
        data: { skipAuth: true, publicRoute: true },
    },
    // Must stay last, and must stay at this level: the layout's parent route is a bare
    // `path: ''` declared above the technician, verify and access-denied routes, so a
    // wildcard inside LAYOUT_ROUTES would make that parent match every URL and swallow them.
    {
        path: '**',
        redirectTo: wildcardRedirect,
    },
];

/**
 * A stale staff deep link belongs on the launcher, but an unmatched *customer* link must not go
 * there: `''` is guarded, and redirects are applied before guards run, so RoleGuard would no
 * longer see the public URL and would push the customer into the staff Keycloak login. See
 * `public-links.ts`.
 */
export function wildcardRedirect(redirectData: { url: UrlSegment[] }): string {
    return isPublicLinkUrl(redirectData.url) ? '/link-unavailable' : '';
}
